// TAPINTO® Editor Frontend - Button Component
// Version: 1.0
// Date: 2025-10-01

import React from 'react';
import { useNode } from '@craftjs/core';
import { ComponentProps } from '../../../types';

interface ButtonProps extends ComponentProps {
  text: string;
  variant: 'primary' | 'secondary' | 'outline' | 'ghost';
  size: 'sm' | 'md' | 'lg';
  href?: string;
  target?: '_blank' | '_self';
  backgroundColor: string;
  textColor: string;
  borderRadius: string;
  padding: string;
  fontSize: string;
  fontWeight: string;
}

export function Button({
  text,
  variant,
  size,
  href,
  target,
  backgroundColor,
  textColor,
  borderRadius,
  padding,
  fontSize,
  fontWeight,
  ...props
}: ButtonProps) {
  const {
    connectors: { connect, drag },
    isActive,
    isHovered,
  } = useNode((state) => ({
    isActive: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const handleClick = (e: React.MouseEvent) => {
    if (href) {
      e.preventDefault();
      if (target === '_blank') {
        window.open(href, '_blank');
      } else {
        window.location.href = href;
      }
    }
  };

  const baseClasses = `
    inline-flex items-center justify-center font-medium transition-colors
    focus:outline-none focus:ring-2 focus:ring-offset-2
    ${isActive ? 'ring-2 ring-primary-500' : ''}
    ${isHovered ? 'ring-2 ring-primary-300' : ''}
  `;

  const variantClasses = {
    primary: 'bg-primary-500 hover:bg-primary-600 text-white focus:ring-primary-500',
    secondary: 'bg-neutral-100 hover:bg-neutral-200 text-neutral-900 focus:ring-neutral-500',
    outline: 'border border-neutral-300 bg-transparent hover:bg-neutral-50 text-neutral-700 focus:ring-neutral-500',
    ghost: 'bg-transparent hover:bg-neutral-100 text-neutral-700 focus:ring-neutral-500',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  return (
    <button
      ref={(ref) => connect(drag(ref))}
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
      `}
      style={{
        backgroundColor: variant === 'primary' ? backgroundColor : undefined,
        color: variant === 'primary' ? textColor : undefined,
        borderRadius,
        padding,
        fontSize,
        fontWeight,
      }}
      onClick={handleClick}
      {...props}
    >
      {text}
    </button>
  );
}

Button.craft = {
  displayName: 'Button',
  props: {
    text: 'Click me',
    variant: 'primary',
    size: 'md',
    href: '',
    target: '_self',
    backgroundColor: '#1B1464',
    textColor: '#FFFFFF',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '16px',
    fontWeight: '500',
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
    canMoveOut: () => true,
  },
};
